// The result moment of the casino (R2115, casino.md §9 "The result
// moment").
//
// Every game screen states the reader's own result in ONE form: the result
// tag. The tag carries the signed figure in the figure step and a word
// beside it, so the result reads without its colour: "+40 WON" in the Good
// ink, "−40 LOST" in the Bad ink, and "±0 PUSH" or "±0 EVEN" in the Text
// ink. A hand the reader had no stake on draws NO tag: the line says that
// there was no bet, in the Dim ink, because a settled hand with no stake
// is neither a win nor a loss.
//
// **The win flash.** What won — the pocket of the ball, the seat that beat
// the dealer, the winning hold'em seat — and the tag itself wear one
// short flash, then they stand still. A screen marks each of them with
// `data-flash="<result counter>"`, and `CasinoPanel` puts the `flash` class
// on a marked element ONE time for each result: a part that the panel
// writes again later carries no `flash` class, so the flash never plays a
// second time. Under reduced motion the panel puts no class on, and the
// highlight stands at once.
//
// **The speech.** The result line of each screen carries `data-say` with
// the same counter and the sentence in `data-speech`. The panel writes
// that sentence ONE time into its one polite live region, which it keeps
// outside the parts, because a part that the panel writes again is a new
// element and a screen reader does not say a region that is new.
//
// The module holds no state and writes no DOM. Every word comes from
// `gen/strings`.

import { overlay as S } from '../../gen/strings';
import type { CasinoResult } from '../../gen/proto/aquaphobia';
import { groupThousands } from '../../locale/group';
import { esc } from '../esc';

/** The tone of one delta: up, down or even. The class of the tag. */
type DeltaTone = 'up' | 'down' | 'even';

function toneOf(delta: number): DeltaTone {
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'even';
}

/** The signed figure, with the minus sign and the thin-space groups. */
export function signedFigure(delta: number): string {
  const amount = groupThousands(Math.abs(Math.trunc(delta)));
  if (delta > 0) return S.casino_delta_up({ amount });
  if (delta < 0) return S.casino_delta_down({ amount });
  return S.casino_delta_even;
}

/** The word of a hand's result. A move of 0 is a push at blackjack and
 *  even everywhere else. */
export function handWord(delta: number, push = false): string {
  if (delta > 0) return S.casino_word_won;
  if (delta < 0) return S.casino_word_lost;
  return push ? S.casino_word_push : S.casino_word_even;
}

/** The word of a sum, for the visit net. */
function netWord(delta: number): string {
  if (delta > 0) return S.casino_word_up;
  if (delta < 0) return S.casino_word_down;
  return S.casino_word_even;
}

/**
 * The result tag: the signed figure and the word, in the ink of its tone.
 * `flash` is the result counter of the win flash, or 0 for a tag that does
 * not flash (the visit net).
 */
export function resultTag(delta: number, word: string, flash = 0): string {
  const mark = flash > 0 ? ` data-flash="${flash}"` : '';
  return `<span class="cd ${toneOf(delta)}"${mark}>`
    + `<b class="cd-n">${signedFigure(delta)}</b> <span class="cd-w">${word}</span></span>`;
}

/** The sentence a screen reader says for one hand's result. */
export function tagSpeech(delta: number, word: string): string {
  return `${signedFigure(delta)} ${word}`;
}

/**
 * The two attributes of a result line: the counter of its result and the
 * sentence the live region says. The sentence is escaped, because it
 * stands inside an attribute.
 */
export function sayAttrs(seq: number, speech: string): string {
  return ` data-say="${seq}" data-speech="${esc(speech)}"`;
}

/** The seats of one result that won: a row with a positive move. */
export function wonSeats(r: CasinoResult | null): Set<number> {
  const out = new Set<number>();
  if (!r || r.aborted) return out;
  for (const row of r.seats) if (row.delta > 0) out.add(row.seat);
  return out;
}

/**
 * The line of the visit net: "net this visit" and its tag. It draws
 * nothing before the reader played one hand in the visit, because a sum
 * of no hands states nothing.
 */
export function visitLine(net: number, hands: number): string {
  if (hands <= 0) return '';
  return `<span class="visit"><span class="visit-k">${S.casino_visit_net}</span>`
    + `${resultTag(net, netWord(net))}</span>`;
}
